import React, { useState } from 'react';
import { Calendar, Star, Filter, Plus, GitBranch, Target, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card.js';


interface MemoryItem {
  id: string;
  text: string;
  tags?: string[];
  metadata?: {
    source?: string;
    createdAt?: string;
  };
}

interface PlannedFeaturesTabProps {
  searchTerm: string;
  selectedAgent: string;
  selectedType: string;
  items: MemoryItem[];
  loading: boolean;
  error: string | null;
  onDelete: (itemId: string) => void;
  onRefresh: () => void;
}

const PlannedFeaturesTab: React.FC<PlannedFeaturesTabProps> = ({ searchTerm, selectedAgent, selectedType, items, loading, error, onDelete, onRefresh }) => {
  const features = items.map(item => ({
    id: item.id,
    title: item.text ? (item.text.length > 50 ? item.text.substring(0, 50) + '...' : item.text) : 'Untitled Feature',
    description: item.text || 'No description available',
    priority: 'Medium',
    status: 'Planning',
    assignee: item.metadata?.source || 'System Team',
    estimatedEffort: '4 weeks',
    targetRelease: '2024-Q3',
    tags: item.tags || [],
    votes: Math.floor(Math.random() * 100) + 50,
    comments: Math.floor(Math.random() * 30) + 5,
    agent: item.metadata?.source || 'Unknown Agent',
    type: 'Feature',
    complexity: 'Medium'
  }));

  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('votes');

  const filteredFeatures = features.filter(feature => {
    const matchesSearch = feature.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         feature.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         feature.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesAgent = selectedAgent === 'All' || feature.agent === selectedAgent;
    const matchesType = selectedType === 'All' || feature.type === selectedType;
    const matchesFilter = filter === 'all' || feature.priority.toLowerCase() === filter;
    
    return matchesSearch && matchesAgent && matchesType && matchesFilter;
  });

  const sortedFeatures = [...filteredFeatures].sort((a, b) => {
    switch (sortBy) {
      case 'priority': {
        const priorityOrder: Record<string, number> = { 'High': 3, 'Medium': 2, 'Low': 1, 'Critical': 4 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      case 'comments':
        return b.comments - a.comments;
      case 'votes':
      default:
        return b.votes - a.votes;
    }
  });

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'bg-red-400/20 text-red-400';
      case 'medium':
        return 'bg-yellow-400/20 text-yellow-400';
      case 'low':
        return 'bg-green-400/20 text-green-400';
      default:
        return 'bg-slate-400/20 text-slate-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'planning':
        return 'bg-blue-400/20 text-blue-400';
      case 'research':
        return 'bg-purple-400/20 text-purple-400';
      case 'design':
        return 'bg-orange-400/20 text-orange-400';
      case 'backlog':
        return 'bg-slate-400/20 text-slate-400';
      default:
        return 'bg-slate-400/20 text-slate-400';
    }
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity.toLowerCase()) {
      case 'high':
        return 'bg-red-400/20 text-red-400';
      case 'medium':
        return 'bg-yellow-400/20 text-yellow-400';
      case 'low':
        return 'bg-green-400/20 text-green-400';
      default:
        return 'bg-slate-400/20 text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-400">Loading planned features...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">⚠️ {error}</div>
        <Button onClick={onRefresh} variant="ghost">
          <RefreshCw size={16} className="mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Planned Features</h2>
          <p className="text-slate-400 mt-1">Upcoming features and enhancements roadmap ({features.length} entries)</p>
        </div>
        <div className="flex space-x-2">
          <Button size="sm" variant="ghost" onClick={onRefresh}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button size="sm">
            <Plus size={16} className="mr-2" />
            Propose Feature
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="violett-input"
          >
            <option value="all">All Priorities</option>
            <option value="high">High Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="low">Low Priority</option>
          </select>
        </div>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="violett-input"
        >
          <option value="votes">Most Voted</option>
          <option value="priority">Highest Priority</option>
          <option value="comments">Most Discussed</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sortedFeatures.map((feature) => (
          <Card key={feature.id} className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300 hover:border-brand-primary/40 cursor-pointer">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg cream-gradient-text text-drop-shadow mb-2">{feature.title}</CardTitle>
                  <CardDescription className="cream-gradient-text text-drop-shadow">{feature.description}</CardDescription>
                </div>
                <div className="flex items-center space-x-1 ml-4">
                  <Star size={14} className="text-yellow-400" />
                  <span className="text-sm text-slate-300">{feature.votes}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(feature.priority)}`}>
                    {feature.priority} Priority
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(feature.status)}`}>
                    {feature.status}
                  </span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(feature.complexity)}`}>
                  {feature.complexity} Complexity
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Assignee:</span>
                  <div className="text-brand-text font-medium">{feature.assignee}</div>
                </div>
                <div>
                  <span className="text-slate-400">Effort:</span>
                  <div className="text-brand-text font-medium">{feature.estimatedEffort}</div>
                </div>
                <div>
                  <span className="text-slate-400">Target:</span>
                  <div className="text-brand-text font-medium">{feature.targetRelease}</div>
                </div>
                <div>
                  <span className="text-slate-400">Agent:</span>
                  <div className="text-brand-text font-medium">{feature.agent}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1">
                {feature.tags.map((tag: string) => (
                  <span key={tag} className="px-2 py-1 text-slate-300 text-xs glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded hover:border-slate-600/50 transition-all duration-300">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-xs text-slate-400">
                  <div className="flex items-center space-x-1">
                    <Target size={12} />
                    <span>{feature.type}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <GitBranch size={12} />
                    <span>{feature.comments} comments</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm">Vote</Button>
                  <Button variant="ghost" size="sm">Discuss</Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(feature.id);
                    }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {sortedFeatures.length === 0 && (
        <div className="text-center py-12">
          <Calendar size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No planned features found</h3>
          <p className="text-slate-400">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default PlannedFeaturesTab;
